import { Geo, GeoType } from "@/types/api/Geo";

export const geoTypes: GeoType[] = [
  {
    text: "National",
    value: "national",
    type: "national",
    state: false,
    hasMap: false,
    includeInMarketsOnly: true,
  },
  {
    text: "State",
    value: "state",
    type: "states",
    state: true,
    hasMap: true,
    idName: "state_id",
    includeInMarketsOnly: false,
  },
  {
    text: "City",
    value: "city",
    type: "cities",
    state: true,
    hasMap: false,
    idName: "city_id",
    includeInMarketsOnly: false,
  },
  {
    text: "County",
    value: "county",
    type: "counties",
    state: true,
    hasMap: true,
    idName: "county_id",
    includeInMarketsOnly: false,
  },
  {
    text: "Zip Code",
    value: "zip_code",
    type: "zip_codes",
    state: true,
    hasMap: false,
    altName: "zipcode",
    idName: "zip_code_id",
    includeInMarketsOnly: false,
  },
  {
    text: "Media Market",
    value: "dma",
    type: "dmas",
    state: false,
    hasMap: true,
    includeInMarketsOnly: true,
  },
  {
    text: "Media Market by State",
    value: "dma_states",
    type: "dma_states",
    state: true,
    hasMap: false,
    idName: "dma_states_id",
    includeInMarketsOnly: true,
  },
  {
    text: "CD",
    value: "cd_nextelection",
    type: "congressional_district_nextelections",
    state: true,
    hasMap: true,
    altName: "congressionaldistrict",
    idName: "congressional_district_nextelection_id",
    includeInMarketsOnly: true,
  },
  {
    text: "State Senate",
    value: "sd_nextelection",
    type: "senate_district_nextelections",
    state: true,
    hasMap: false,
    idName: "senate_district_nextelection_id",
    includeInMarketsOnly: false,
  },
  {
    text: "State Legislature",
    value: "ld_nextelection",
    type: "legislative_district_nextelections",
    state: true,
    hasMap: false,
    idName: "legislative_district_nextelection_id",
    includeInMarketsOnly: false,
  },
];

const DMA_CD_TYPE = "dma_congressional_district_nextelections";

/** Normalize geography type strings for comparison with GeoType.type. */
export function getGeoType(geoType: string): string {
  if (geoType === DMA_CD_TYPE) {
    return "congressional_district_nextelections";
  }
  // Some states return jurisnames from the cities endpoint; treat as cities in the UI.
  if (geoType === "jurisnames") {
    return "cities";
  }
  return geoType;
}

/**
 * TV ratings CD markets are DMA-CD rows; client limits are CD rows.
 * Match by CD number + state when market.ref_id is the DMA-CD id.
 */
export function congressionalDistrictLimitKey(geo: {
  name?: string | null;
  state_id?: number | null;
}): string | null {
  if (geo.state_id == null || geo.name == null || geo.name === "") {
    return null;
  }
  const name = String(geo.name).trim();
  if (name === "0" || /^at large\b/i.test(name)) {
    return `${geo.state_id}:at-large`;
  }
  const marketCd = name.match(/\bCD\s+(\d+)\b/i);
  if (marketCd) {
    return `${geo.state_id}:${marketCd[1] === "0" ? "at-large" : marketCd[1]}`;
  }
  if (/^\d+$/.test(name)) {
    return `${geo.state_id}:${name}`;
  }
  return null;
}

export const formatGeos = (geos: Geo[]) => {
  return geos.map((g) => {
    const type = geoTypes.find((t) => t.value === g.type);
    let formattedGeo = `${type?.text}`;
    if (g.type === "national") {
      return formattedGeo;
    }
    if (
      type &&
      ["CD", "State Senate", "State Legislature"].includes(type.text)
    ) {
      return (formattedGeo += `: ${g.state_name} ${g.name}`);
    }
    formattedGeo += g.state_name
      ? `: ${g.name}, ${g.state_name}`
      : `: ${g.name}`;
    return formattedGeo;
  });
};
